import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { HomeIcon } from '@heroicons/react/24/outline';

import { useAuth } from '@hooks/useAuth';
import { useTheme } from '../../contexts/ThemeContext';
import { Button } from '@components/Button';
import { CartIcon } from '@components/CartIcon';
import { ThemeToggle } from '../ThemeToggle';
import { ROUTES } from '@lib/routes';
import { UserRole } from '@shared/types/user';

export function Header(): JSX.Element {
  const { isAuthenticated, user } = useAuth();
  const { siteSettings, theme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const handleSignOut = async (): Promise<void> => {
    const { signOut } = await import('@lib/firebase');
    await signOut();
    navigate(ROUTES.HOME);
    setIsMobileMenuOpen(false);
  };

  // Use site settings navigation or fallback to defaults
  const navLinks = siteSettings?.header.navigation
    .filter((item: any) => item.visible)
    .sort((a: any, b: any) => a.order - b.order)
    .map((item: any) => ({ to: item.url, label: item.label, openInNewTab: item.openInNewTab })) || [
    { to: ROUTES.CUSTOMER.SEARCH, label: 'Search Hubs', openInNewTab: false },
    { to: ROUTES.BLOG, label: 'Blog', openInNewTab: false },
    { to: ROUTES.CUSTOMER.PRODUCTS, label: 'Store', openInNewTab: false },
  ];

  const isAdmin = user?.role === UserRole.ADMIN;

  const authNavLinks = isAuthenticated && siteSettings?.header.authenticatedNavigation
    ? siteSettings.header.authenticatedNavigation
        .filter((item: any) => item.visible)
        .sort((a: any, b: any) => a.order - b.order)
        .map((item: any) => ({ to: item.url, label: item.label, openInNewTab: item.openInNewTab }))
    : isAuthenticated
    ? [
        { to: ROUTES.CUSTOMER.MY_BOOKINGS, label: 'My Bookings', openInNewTab: false },
        { to: ROUTES.CUSTOMER.PROFILE, label: 'Profile', openInNewTab: false },
      ]
    : [];

  return (
    <>
      <header className="bg-white/95 dark:bg-gray-900/90 backdrop-blur-2xl border-b border-gray-200/70 dark:border-gray-800/60 shadow-sm sticky top-0 z-50">
        <nav className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link
              to={ROUTES.HOME}
              className="flex items-center gap-2 hover:opacity-80 active:opacity-60 transition-all duration-200 hover:scale-105 active:scale-95"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {(() => {
                // Get the appropriate logo URL based on theme
                const logoUrl = theme === 'dark' 
                  ? (siteSettings?.header.logo.darkUrl || siteSettings?.header.logo.url)
                  : (siteSettings?.header.logo.lightUrl || siteSettings?.header.logo.url);
                
                return logoUrl ? (
                  <img 
                    src={logoUrl} 
                    alt={siteSettings?.header.logo.alt || 'Logo'} 
                    style={{
                      width: siteSettings?.header.logo.width ? `${siteSettings.header.logo.width}px` : 'auto',
                      height: siteSettings?.header.logo.height ? `${siteSettings.header.logo.height}px` : 'auto',
                    }}
                    className="max-h-12 object-contain"
                  />
                ) : (
                  <span className="text-xl sm:text-2xl logo-font text-site-accent">
                    {siteSettings?.header.logo.alt || 'RAVEN BIKES'}
                  </span>
                );
              })()}
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-4 md:gap-6 flex-wrap">
              {navLinks.map((link: any, index: number) => (
                link.openInNewTab ? (
                  <a
                    key={`${link.to}-${index}`}
                    href={link.to}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:text-site-accent font-medium transition-all duration-200 hover:scale-105 active:scale-95 hover:brightness-110 whitespace-nowrap"
                  >
                    {link.label}
                  </a>
                ) : (
                  <Link
                    key={`${link.to}-${index}`}
                    to={link.to}
                    className="text-primary hover:text-site-accent font-medium transition-all duration-200 hover:scale-105 active:scale-95 hover:brightness-110 whitespace-nowrap"
                  >
                    {link.label}
                  </Link>
                )
              ))}

              {isAuthenticated ? (
                <>
                  {authNavLinks.map((link) => (
                    <Link
                      key={link.to}
                      to={link.to}
                      className="text-gray-700 dark:text-gray-300 hover:text-site-accent font-medium transition-all duration-200 hover:scale-105 active:scale-95 hover:brightness-110"
                    >
                      {link.label}
                    </Link>
                  ))}
                  <CartIcon />
                  <ThemeToggle />
                  <Button variant="outline" size="sm" onClick={handleSignOut}>
                    Sign Out
                  </Button>
                  {isAdmin && (
                    <Link to={ROUTES.ADMIN.DASHBOARD}>
                      <Button variant="ghost" size="sm" className="gap-2 font-semibold">
                        <HomeIcon className="w-4 h-4" aria-hidden="true" />
                        Admin
                      </Button>
                    </Link>
                  )}
                </>
              ) : (
                <>
                  <CartIcon />
                  <ThemeToggle />
                  <Link to={ROUTES.LOGIN}>
                    <Button variant="ghost" size="sm">
                      Sign In
                    </Button>
                  </Link>
                  {siteSettings?.header.showCta && siteSettings.header.ctaUrl ? (
                    <a href={siteSettings.header.ctaUrl}>
                      <Button variant="primary" size="sm">
                        {siteSettings.header.ctaText || 'Sign Up'}
                      </Button>
                    </a>
                  ) : (
                    <Link to={ROUTES.SIGNUP}>
                      <Button variant="primary" size="sm">
                        Sign Up
                      </Button>
                    </Link>
                  )}
                </>
              )}
            </div>

            {/* Mobile: Theme + Cart + Hamburger */}
            <div className="flex items-center gap-3 lg:hidden">
              <ThemeToggle />
              <CartIcon />
              <button
                type="button"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2 rounded-lg text-primary hover:bg-tertiary focus:outline-none focus:ring-2 focus:ring-site-accent min-w-[44px] min-h-[44px] touch-manipulation flex items-center justify-center"
                aria-label="Toggle menu"
                aria-expanded={isMobileMenuOpen}
              >
                {isMobileMenuOpen ? (
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                ) : (
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </nav>
      </header>

      {/* Mobile Slide-Out Navigation */}
      <div
        className={`
          fixed inset-0 z-50 lg:hidden
          transition-transform duration-300 ease-in-out
          ${isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}
        `}
      >
        {/* Backdrop/Overlay */}
        <div
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={() => setIsMobileMenuOpen(false)}
          aria-hidden="true"
        />

        {/* Slide-Out Panel */}
        <div className="absolute right-0 top-0 h-full w-80 max-w-[85vw] bg-primary shadow-xl overflow-y-auto">
          <div className="flex flex-col h-full">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-primary">Menu</h2>
              <button
                type="button"
                onClick={() => setIsMobileMenuOpen(false)}
                className="p-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-purple-500 min-w-[44px] min-h-[44px] touch-manipulation"
                aria-label="Close menu"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Navigation Links */}
            <nav className="flex-1 p-4 space-y-2">
              {navLinks.map((link: any, index: number) => (
                link.openInNewTab ? (
                  <a
                    key={`${link.to}-${index}`}
                    href={link.to}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block px-4 py-3 rounded-lg text-primary hover:bg-tertiary hover:text-site-accent font-medium transition-colors min-h-[44px] touch-manipulation flex items-center"
                  >
                    {link.label}
                  </a>
                ) : (
                  <Link
                    key={`${link.to}-${index}`}
                    to={link.to}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block px-4 py-3 rounded-lg text-primary hover:bg-tertiary hover:text-site-accent font-medium transition-colors min-h-[44px] touch-manipulation flex items-center"
                  >
                    {link.label}
                  </Link>
                )
              ))}

              {/* Divider */}
              <div className="border-t border-gray-200 dark:border-gray-700 my-4" />

              {isAuthenticated ? (
                <>
                  {authNavLinks.map((link) => (
                    <Link
                      key={link.to}
                      to={link.to}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="block px-4 py-3 rounded-lg text-primary hover:bg-tertiary active:bg-tertiary/70 hover:text-site-accent active:text-site-accent/80 font-medium transition-all duration-200 min-h-[44px] touch-manipulation flex items-center"
                    >
                      {link.label}
                    </Link>
                  ))}
                  <button
                    type="button"
                    onClick={handleSignOut}
                    className="w-full px-4 py-3 rounded-lg text-left text-theme-error hover:bg-theme-error/10 font-medium transition-colors min-h-[44px] touch-manipulation"
                  >
                    Sign Out
                  </button>
                  {isAdmin && (
                    <Link
                      to={ROUTES.ADMIN.DASHBOARD}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="block px-4 py-3 rounded-lg bg-site-primary text-white hover:bg-site-accent font-semibold transition-colors min-h-[44px] touch-manipulation flex items-center gap-2"
                    >
                      <HomeIcon className="w-5 h-5" aria-hidden="true" />
                      Admin
                    </Link>
                  )}
                </>
              ) : (
                <>
                  <Link
                    to={ROUTES.LOGIN}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block px-4 py-3 rounded-lg text-primary hover:bg-tertiary hover:text-site-accent font-medium transition-colors min-h-[44px] touch-manipulation flex items-center"
                  >
                    Sign In
                  </Link>
                  {siteSettings?.header.showCta && siteSettings.header.ctaUrl ? (
                    <a
                      href={siteSettings.header.ctaUrl}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="block px-4 py-3 rounded-lg bg-site-accent text-white hover:bg-site-accent/80 font-medium transition-colors min-h-[44px] touch-manipulation flex items-center justify-center mt-2"
                    >
                      {siteSettings.header.ctaText || 'Sign Up'}
                    </a>
                  ) : (
                    <Link
                      to={ROUTES.SIGNUP}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="block px-4 py-3 rounded-lg bg-site-accent text-white hover:bg-site-accent/80 font-medium transition-colors min-h-[44px] touch-manipulation flex items-center justify-center mt-2"
                    >
                      Sign Up
                    </Link>
                  )}
                </>
              )}
            </nav>
          </div>
        </div>
      </div>
    </>
  );
}


